import { useState, useEffect } from "react";
import { Card, CardContent } from '../../../ui/card';
// import { Button } from '../../../ui/button';
import { Button } from "ui/button";
import { Input } from 'ui/input';
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { Badge } from 'ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'ui/dialog';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Building2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { getPropertyTypes, createPropertyType, updatePropertyType, deletePropertyType } from "services/propertyTypeService";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { removeSpecialCharacters } from "utils/commonutils";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface PropertyType {
  id: string;
  publicId: string;
  code: string;
  propertyType: string;
  status: "active" | "inactive";
  createdOn: string;
}

export const PropertyTypeGradeMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PropertyType | null>(null);
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<PropertyType | null>(null);

  const { canAdd, canEdit, canDelete, screenName, icon: ScreenIcon } = useScreenPermission();

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [formData, setFormData] = useState({
    code: '',
    propertyType: '',
    status: 'active' as 'active' | 'inactive',
  });

  const [editFormData, setEditFormData] = useState({
    code: '',
    propertyType: '',
    status: 'active' as 'active' | 'inactive',
  });

  const [errors, setErrors] = useState({
    code: "",
    propertyType: "",
  });

  // Apply filters

  const fetchPropertyTypes = async () => {
    try {
      setLoading(true);
      const res = await getPropertyTypes({
        searchText: searchTerm || null,
        pageNo,
        pageSize,
      });

      const mappedData: PropertyType[] = res.items.map((item) => ({
        id: item.id.toString(),
        publicId: item.publicId,
        code: item.code,
        propertyType: item.name,
        status: item.isActive ? "active" : "inactive",
        createdOn: item.createdAt,
      }));

      setPropertyTypes(mappedData);
      setTotalCount(res.totalCount);
    } catch (error) {
      toast.error("Failed to load property types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchPropertyTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Apply sorting
  const sortedData = [...propertyTypes].sort((a, b) => {
    if (!sortColumn) return 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let aValue: any = a[sortColumn as keyof PropertyType];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let bValue: any = b[sortColumn as keyof PropertyType];

    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleAdd = async () => {
    const newErrors = {
      code: "",
      propertyType: "",
    };

    if (!formData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (formData.code.length > 20) {
      newErrors.code = "Code cannot exceed 20 characters";
    }

    if (!formData.propertyType.trim()) {
      newErrors.propertyType = "Property Type is required";
    }

    const codeExists = propertyTypes.some(
      (item) => item.code.toLowerCase() === formData.code.toLowerCase()
    );

    if (codeExists) {
      newErrors.code = "Property Code already used for another property.";
    }

    setErrors(newErrors);

    if (newErrors.code || newErrors.propertyType) return;

    try {
      setLoading(true);
      await createPropertyType({
        name: formData.propertyType,
        code: formData.code,
        isActive: formData.status === "active",
        createdBy: "1",
      });

      toast.success("Property Type added successfully");

      setIsAddDialogOpen(false);
      resetForm();
      fetchPropertyTypes();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to create property type";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: PropertyType) => {
    setEditingItem(item);
    setEditFormData({
      code: item.code,
      propertyType: item.propertyType,
      status: item.status,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingItem) return;

    const newErrors = {
      code: "",
      propertyType: "",
    };

    if (!editFormData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (editFormData.code.length > 20) {
      newErrors.code = "Code cannot exceed 20 characters";
    }

    if (!editFormData.propertyType.trim()) {
      newErrors.propertyType = "Property Type is required";
    }

    const codeExists = propertyTypes.some(
      (item) =>
        item.code.toLowerCase() === editFormData.code.toLowerCase() &&
        item.publicId !== editingItem.publicId
    );

    if (codeExists) {
      newErrors.code = "Property Code already used for another property.";
    }

    setErrors(newErrors);

    if (newErrors.code || newErrors.propertyType) return;

    try {
      setLoading(true);
      await updatePropertyType({
        publicId: editingItem.publicId,
        name: editFormData.propertyType,
        code: editFormData.code,
        isActive: editFormData.status === "active",
        updatedBy: "1",
      });

      toast.success("Property Type updated successfully");

      setIsEditDialogOpen(false);
      setEditingItem(null);

      fetchPropertyTypes();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to update property type";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item: PropertyType) => {
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;

    try {
     setLoading(true);
      await deletePropertyType(deletingItem.publicId, "1");

      toast.success("Property Type deleted successfully");

      setIsDeleteDialogOpen(false);
      setDeletingItem(null);

      fetchPropertyTypes();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to delete property type";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      propertyType: "",
      status: "active",
    });

    setErrors({
      code: "",
      propertyType: "",
    });
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search property types..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            {canAdd && (
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                style={{ background: '#5B9BD5' }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Property Type
              </Button>
            )}
          </div>
        </div>

        {/* Desktop Table */}
        <Card className="bg-white border-[#5B9BD5]/30 hidden lg:block">
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: '#E3F2FD' }}>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('code')}
                  >
                    <div className="flex items-center gap-1">
                      Code
                      {sortColumn === 'code' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('propertyType')}
                  >
                    <div className="flex items-center gap-1">
                      Property Type
                      {sortColumn === 'propertyType' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('createdOn')}
                  >
                    <div className="flex items-center gap-1">
                      Created Date
                      {sortColumn === 'createdOn' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-center">
                    Status
                  </TableHead>
                  {(canEdit || canDelete) && (
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-right">
                      Actions
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 px-4 text-gray-500 text-sm">
                      {searchTerm ? 'No property types found matching your search' : 'No property types found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((item) => (
                    <TableRow key={item.id} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-900 text-sm font-medium">
                          {item.code}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-[#5B9BD5]" />
                          </div>
                          <span className="text-gray-900 text-sm font-medium">
                            {item.propertyType}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <span className="text-gray-700 text-sm">
                          {new Date(item.createdOn).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center">
                        <Badge
                          variant="outline"
                          className={`${item.status === 'active'
                              ? 'bg-green-100 text-green-700 border-green-200'
                              : 'bg-gray-100 text-gray-700 border-gray-200'
                            } text-xs`}
                        >
                          {item.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          {canEdit && (
                            <Button
                              onClick={() => handleEdit(item)}
                              size="sm"
                              variant="ghost"
                              className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8 p-0"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              onClick={() => handleDelete(item)}
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="property types"
          />
        </Card>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {sortedData.length === 0 ? (
            <Card className="border-[#5B9BD5]/30">
              <CardContent className="p-8 text-center">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">
                  {searchTerm ? 'No property types found matching your search' : 'No property types found'}
                </p>
              </CardContent>
            </Card>
          ) : (
            sortedData.map((item) => (
              <Card key={item.id} className="border-[#5B9BD5]/30 shadow-sm">
                <CardContent style={{ padding: "10px" }}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#5B9BD5]" />
                      </div>
                      <div>
                        <span className="text-xs font-mono font-medium text-gray-600 bg-gray-100 rounded">
                          {item.code}
                        </span>
                        <h3 className="text-gray-900 text-sm">{item.propertyType}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(item.createdOn).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Badge
                        variant="outline"
                        className={`${item.status === 'active'
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-gray-100 text-gray-700 border-gray-200'
                          } text-xs`}
                      >
                        {item.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                      <div className="flex justify-end border-t border-gray-100">
                        {canEdit && (
                          <Button
                            onClick={() => handleEdit(item)}
                            size="sm"
                            variant="ghost"
                            className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8"
                            style={{ padding: "0px", gap: "0px" }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            onClick={() => handleDelete(item)}
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                            style={{ padding: "0px", gap: "0px" }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="property types"
          />
        </div>



        {/* Add Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="bg-white sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle style={{ color: '#5B9BD5' }}>Add Property Type</DialogTitle>
              <DialogDescription>
                Create a new property type
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
              <InputFieldset
                  label="Code"
                  required
                  id="code"
                  value={formData.code}
                  onChange={(e) => {
                    setFormData({ ...formData, code: e.target.value });
                    setErrors({ ...errors, code: "" });
                  }}
                  placeholder="e.g., HTL, MAW, APT"
                  className={`bg-white ${
                    errors.code ? "border-red-500 focus:ring-red-500" : "border-gray-200"
                  }`}
                />

                {errors.code && (
                  <p className="text-red-500 text-xs">{errors.code}</p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Property Type"
                  required
                  id="propertyType"
                  value={formData.propertyType}
                  onChange={(e) => {
                    const value = removeSpecialCharacters(e.target.value)
                    setFormData({ ...formData, propertyType: value });
                    setErrors({ ...errors, propertyType: "" });
                  }}
                  placeholder="e.g., Hotel, Mawaid, Apartment"
                  className={`bg-white ${
                    errors.propertyType
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200"
                    }`}
                />

                {errors.propertyType && (
                  <p className="text-red-500 text-xs">{errors.propertyType}</p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Status">
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                  className="w-full h-6 bg-white text-sm"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                style={{ background: '#5B9BD5' }}
                className="text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Property Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="bg-white sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle style={{ color: '#5B9BD5' }}>Edit Property Type</DialogTitle>
              <DialogDescription>
                Update property type details
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Code"
                  required
                  id="edit-code"
                  value={editFormData.code}
                  onChange={(e) => {
                    setEditFormData({ ...editFormData, code: e.target.value });
                    setErrors({ ...errors, code: "" });
                  }}
                  className={`bg-white ${
                    errors.code ? "border-red-500 focus:ring-red-500" : "border-gray-200"
                  }`}
                />

                {errors.code && (
                  <p className="text-red-500 text-xs">{errors.code}</p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Property Type"
                  required
                  id="edit-propertyType"
                  value={editFormData.propertyType}
                  onChange={(e) => {
                    const value = removeSpecialCharacters(e.target.value)
                    setEditFormData({
                      ...editFormData,
                      propertyType: value,
                    });
                    setErrors({ ...errors, propertyType: "" });
                  }}
                  className={`bg-white ${
                    errors.propertyType
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200"
                    }`}
                />

                {errors.propertyType && (
                  <p className="text-red-500 text-xs">{errors.propertyType}</p>
                )}
              </div>
              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <select
                    id="edit-status"
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as 'active' | 'inactive' })}
                    className="w-full h-6 bg-white text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingItem(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                style={{ background: '#5B9BD5' }}
                className="text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Update Property Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Property Type
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingItem?.propertyType}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};